/* Elemental Wear product page behaviour — mirrors neptun2021.de (Prestige): swipe gallery with
   adaptive height + dots, desktop thumbnails that follow the scroll, size swatches that update
   price / URL / button, one-at-a-time accordions, share pills, and a full-screen zoom viewer.
   Add-to-cart itself stays on Dawn's <product-form> (cart plumbing). */
(() => {
  if (customElements.get('ew-product')) return;

  const DESK = window.matchMedia('(min-width: 1008px)');

  const formatMoney = (cents, format) => {
    const fmt = (n, dec, th, dc) => {
      const [i, d] = (n / 100).toFixed(dec).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, th) + (d ? dc + d : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, k) => ({
      amount: fmt(cents, 2, ',', '.'),
      amount_no_decimals: fmt(cents, 0, ',', '.'),
      amount_with_comma_separator: fmt(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: fmt(cents, 0, '.', ','),
      amount_with_apostrophe_separator: fmt(cents, 2, "'", '.'),
    }[k] ?? fmt(cents, 2, ',', '.')));
  };

  class EwProduct extends HTMLElement {
    connectedCallback() {
      this.product = JSON.parse(this.querySelector('[data-ew-json]').textContent);
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.track = this.querySelector('.ew-p__slides');
      this.slides = [...this.querySelectorAll('.ew-p__slide')];
      this.dots = [...this.querySelectorAll('.ew-p__dot')];
      this.vdots = [...this.querySelectorAll('.ew-p__vdot')];
      this.thumbs = [...this.querySelectorAll('.ew-p__thumb')];
      this.index = 0;

      // photos 2+ (data-ew-lazy-manual): desktop = neptun's reveal (load 100px inside the screen, then rise in); phone = load now
      this.querySelectorAll('.ew-p__img[data-ew-lazy-manual]').forEach((img) => {
        if (DESK.matches && window.ewLazy) window.ewLazy.watch(img); else if (window.ewLazy) window.ewLazy.load(img);
        else { img.srcset = img.dataset.srcset || ''; img.src = img.dataset.src || img.src; }
      });
      this.querySelectorAll('.ew-p__img, .ew-p__thumb img').forEach((img) => {
        const done = () => { img.classList.add('is-loaded'); if (img.closest('.ew-p__slide') === this.slides[this.index]) this.adapt(); };
        if (img.complete && img.naturalWidth) done();
        else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); }
      });

      // gallery
      this.track.addEventListener('scroll', () => {
        if (DESK.matches) return;
        clearTimeout(this.scrollT);
        this.scrollT = setTimeout(() => {
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          if (i !== this.index) this.setIndex(i);
        }, 60);
      }, { passive: true });
      this.querySelector('.ew-p__arrow--prev')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('.ew-p__arrow--next')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));
      [...this.thumbs, ...this.vdots].forEach((t) => t.addEventListener('click', (e) => { e.preventDefault(); this.go(+t.dataset.index); }));
      this.slides.forEach((s, i) => s.querySelector('.ew-p__img')?.addEventListener('click', () => this.zoomOpen(i)));
      window.addEventListener('resize', () => this.adapt());
      DESK.addEventListener('change', () => this.adapt());
      if ('IntersectionObserver' in window) {
        const seen = new Map();
        this.io = new IntersectionObserver((entries) => {
          if (!DESK.matches) return;
          entries.forEach((e) => seen.set(e.target, e.intersectionRatio));
          let best = -1, bestR = 0;
          this.slides.forEach((s, i) => { const r = seen.get(s) || 0; if (r > bestR) { bestR = r; best = i; } });
          if (best > -1 && best !== this.index) this.setIndex(best, true);
        }, { threshold: [0, 0.25, 0.5, 0.75, 1] });
        this.slides.forEach((s) => this.io.observe(s));
      }
      this.adapt();

      // variants
      this.idInput = this.querySelector('input[name="id"]');
      this.querySelectorAll('.ew-p__radio').forEach((r) => r.addEventListener('change', () => this.onOption()));

      // accordions
      this.querySelectorAll('.ew-p__acc-title').forEach((t) => {
        t.addEventListener('click', () => this.toggleAcc(t));
        t.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.toggleAcc(t); } });
      });

      // share
      this.shareBtn = this.querySelector('.ew-p__action--share');
      this.shareList = this.querySelector('.ew-p__share-list');
      this.shareBtn?.addEventListener('click', () => this.toggleShare());

      // zoom
      this.zoom = this.querySelector('.ew-zoom');
      this.querySelector('.ew-p__action--zoom')?.addEventListener('click', () => this.zoomOpen(this.index));
      if (this.zoom) {
        document.body.appendChild(this.zoom);
        this.zTrack = this.zoom.querySelector('.ew-zoom__track');
        this.zPrev = this.zoom.querySelector('.ew-zoom__prev');
        this.zNext = this.zoom.querySelector('.ew-zoom__next');
        this.zoom.querySelector('.ew-zoom__close').addEventListener('click', () => this.zoomClose());
        this.zPrev.addEventListener('click', () => this.zoomGo(this.zIndex - 1));
        this.zNext.addEventListener('click', () => this.zoomGo(this.zIndex + 1));
        this.zTrack.addEventListener('scroll', () => {
          clearTimeout(this.zT);
          this.zT = setTimeout(() => { this.zIndex = Math.round(this.zTrack.scrollLeft / this.zTrack.clientWidth); this.zoomButtons(); }, 60);
        }, { passive: true });
        this.zoom.querySelectorAll('.ew-zoom__item img').forEach((img) => img.addEventListener('click', () => img.classList.toggle('is-zoomed')));
        document.addEventListener('keydown', (e) => {
          if (!this.zoom.classList.contains('is-open')) return;
          if (e.key === 'Escape') this.zoomClose();
          if (e.key === 'ArrowRight') this.zoomGo(this.zIndex + 1);
          if (e.key === 'ArrowLeft') this.zoomGo(this.zIndex - 1);
        });
      }
    }

    go(i) {
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      if (DESK.matches) {
        const top = this.slides[i].getBoundingClientRect().top + window.scrollY - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ew-header-h')) || 0) - (parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--ew-bar-h')) || 0) - 25;
        window.scrollTo({ top, behavior: 'smooth' });
      } else {
        this.track.scrollTo({ left: this.slides[i].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
      this.setIndex(i);
    }

    setIndex(i, fromScroll) {
      this.index = i;
      this.dots.forEach((d, k) => d.classList.toggle('is-selected', k === i));
      this.vdots.forEach((d, k) => d.classList.toggle('is-selected', k === i));
      this.thumbs.forEach((t, k) => t.classList.toggle('is-selected', k === i));
      if (!fromScroll) this.adapt();
    }

    // Aayan 2026-09-25: the viewport stays at the TALLEST photo's height (nothing below jumps when you swipe);
    // shorter photos sit vertically centred (CSS align-items:center on .ew-p__slides)
    adapt() {
      if (DESK.matches) { this.track.style.height = ''; return; }
      const h = Math.max(0, ...this.slides.map((s) => s.offsetHeight));
      if (h) this.track.style.height = h + 'px';
    }

    onOption() {
      const chosen = [...this.querySelectorAll('.ew-p__option')].map((o) => o.querySelector('.ew-p__radio:checked')?.value);
      const v = this.product.variants.find((x) => x.options.every((val, k) => val === chosen[k]));
      const btn = this.querySelector('.ew-p__atc');
      const label = btn.querySelector('span');
      if (!v) { btn.disabled = true; label.textContent = this.dataset.tUnavailable; return; }
      this.idInput.value = v.id;
      const price = this.querySelector('.ew-p__price:not(.ew-p__price--compare)');
      const cmp = this.querySelector('.ew-p__price--compare');
      price.textContent = formatMoney(v.price, this.moneyFormat);
      const onSale = v.compare_at_price && v.compare_at_price > v.price;
      price.classList.toggle('ew-p__price--highlight', !!onSale);
      cmp.hidden = !onSale;
      if (onSale) cmp.textContent = formatMoney(v.compare_at_price, this.moneyFormat);
      btn.disabled = !v.available;
      label.textContent = v.available ? this.dataset.tAdd : this.dataset.tSoldOut;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url.toString());
      if (v.featured_media) {
        const i = this.slides.findIndex((s) => s.dataset.mediaId === String(v.featured_media.id));
        if (i > -1 && i !== this.index) this.go(i);
      }
    }

    toggleAcc(title) {
      const body = title.nextElementSibling;
      const open = body.classList.contains('is-active');
      this.querySelectorAll('.ew-p__acc-body.is-active').forEach((b) => {
        b.classList.remove('is-active');
        b.previousElementSibling.querySelector('.ew-p__sign').textContent = '+';
      });
      if (!open) { body.classList.add('is-active'); title.querySelector('.ew-p__sign').textContent = '-'; }
    }

    toggleShare() {
      const open = this.shareBtn.getAttribute('aria-expanded') === 'true';
      this.shareBtn.setAttribute('aria-expanded', String(!open));
      this.shareBtn.classList.toggle('is-active', !open);
      this.shareBtn.classList.toggle('is-second', !open);
      this.shareList.setAttribute('aria-hidden', String(open));
    }

    zoomOpen(i) {
      if (!this.zoom) return;
      this.zoom.classList.add('is-open');
      this.zIndex = i;
      requestAnimationFrame(() => {
        this.zTrack.scrollLeft = this.zTrack.clientWidth * i;
        this.zoomButtons();
        requestAnimationFrame(() => this.zoom.classList.add('is-visible'));
      });
      document.documentElement.style.overflow = 'hidden';
    }

    zoomGo(i) {
      i = Math.max(0, Math.min(this.slides.length - 1, i));
      this.zIndex = i;
      this.zTrack.scrollTo({ left: this.zTrack.clientWidth * i, behavior: 'smooth' });
      this.zoomButtons();
    }

    zoomButtons() {
      this.zPrev.disabled = this.zIndex <= 0;
      this.zNext.disabled = this.zIndex >= this.slides.length - 1;
    }

    zoomClose() {
      this.zoom.classList.remove('is-visible');
      document.documentElement.style.overflow = '';
      this.zoom.querySelectorAll('img.is-zoomed').forEach((img) => img.classList.remove('is-zoomed'));
      setTimeout(() => this.zoom.classList.remove('is-open'), 500);
    }
  }

  customElements.define('ew-product', EwProduct);
})();
